import React from 'react';
import { gql, useQuery } from '@apollo/client';
import { Box, Button, CircularProgress, Paper, Typography } from '@mui/material';
import { accountSelectedSelector } from 'atoms';
import { useTranslation } from 'react-i18next';
import { useRecoilValue } from 'recoil';
import { Timetable as TimetableType } from 'types/timetable.types';
import HelpModal from './HelpModal';
import fragments from 'services/fragments';
import TimetableCalendar from './TimetableCalendar';
import TimetableList from './TimetableList';

const TIMETABLE = gql`
  ${fragments.TimeTableCalendarDay}
  query TimeTable($isCloud: Boolean!, $masterCompanyId: Int, $accountId: Int) {
    getTimeTable(isCloud: $isCloud, masterCompanyId: $masterCompanyId, accountId: $accountId) {
      calendar {
        sunday {
          ...TimeTableCalendarDayProperties
        }
        monday {
          ...TimeTableCalendarDayProperties
        }
        tuesday {
          ...TimeTableCalendarDayProperties
        }
        wednesday {
          ...TimeTableCalendarDayProperties
        }
        thursday {
          ...TimeTableCalendarDayProperties
        }
        friday {
          ...TimeTableCalendarDayProperties
        }
        saturday {
          ...TimeTableCalendarDayProperties
        }
        holiday {
          ...TimeTableCalendarDayProperties
        }
      }

      list {
        id
        status
        days
        allPartitions
        partitions
        initialHour
        initialHourTolerance
        endingHour
        endingHourTolerance
      }
    }
  }
`;

function TimetablePage() {
  const { t } = useTranslation(['_common', 'timetable']);
  const accountSelected = useRecoilValue(accountSelectedSelector);
  const [helpModalOpen, setHelpModalOpen] = React.useState(false);

  const { data, error, loading } = useQuery<{
    getTimeTable: TimetableType;
  }>(TIMETABLE, {
    variables: {
      ...accountSelected,
      accountId: accountSelected?.id || 0,
    },
  });

  if (error) {
    throw error;
  }

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        mb: 2,
        flex: '0 0 auto',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography variant="h6">{t('_common:timetable')}</Typography>

        <Button variant="contained" onClick={() => setHelpModalOpen(true)}>
          {t('timetable:what_is_the_timetable')}
        </Button>
      </Box>

      <HelpModal
        open={helpModalOpen}
        setOpen={setHelpModalOpen}
        hasCalendar={!!data?.getTimeTable?.calendar}
      />

      {loading ? (
        <CircularProgress sx={{ m: 'auto' }} />
      ) : (
        <>
          {!!data?.getTimeTable?.calendar && (
            <TimetableCalendar data={data.getTimeTable.calendar} />
          )}
          {!!data?.getTimeTable?.list && <TimetableList data={data.getTimeTable.list} />}
        </>
      )}
    </Paper>
  );
}

export default TimetablePage;
